import { VehicleForm } from '@/features/vehicles/components/layout/VehicleForm'
import { CreateVehicleFormSchema, type CreateVehicleForm } from '@/features/vehicles/api'
import { useCreateVehicle } from '@/features/vehicles/hooks'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Container, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMessageSnackbar } from '@/hooks/useMessageSnackbar'
import { ApiErrorAlertBox } from '@/components/ApiAlertBox/ApiErrorAlertBox'
export const Route = createFileRoute('/_authenticated/$companySlug/vehicles/create')({
  beforeLoad: ({ context }) => {
    context.auth.rejectIfMissingPermissions(['VEHICLE_READ', 'VEHICLE_WRITE'])
  },
  component: CreateVehicle
})

function CreateVehicle() {
  const navigate = Route.useNavigate()
  const { t } = useTranslation(['_routes', '_global'])
  const [error, setError] = useState<Error | null>(null)
  const { showSuccess } = useMessageSnackbar()
  const errorRef = useRef<HTMLDivElement>(null)

  const form = useForm<CreateVehicleForm>({
    resolver: zodResolver(CreateVehicleFormSchema),
    defaultValues: {
      contract: { enabled: false }
    }
  })

  const createVehicleMutation = useCreateVehicle({
    onSuccess: name => {
      navigate({
        to: '/$companySlug/vehicles/$vin',
        params: { vin: form.getValues().vin }
      })
      showSuccess(
        t('routes.$fleetId_vehicles_create.main.messages.success', {
          name: name
        })
      )
    },
    onError: error => {
      setError(error)
    }
  })

  const onSubmit = (data: CreateVehicleForm) => {
    setError(null)
    createVehicleMutation.mutate(data)
  }

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [error])

  return (
    <Container maxWidth="xl" sx={{ py: 4, marginLeft: 0 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('routes.$fleetId_vehicles_create.main.form.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('routes.$fleetId_vehicles_create.main.form.subtitle')}
        </Typography>
      </Box>

      {/* Error Messages */}
      {error && <ApiErrorAlertBox ref={errorRef} error={error} sx={{ mb: 2 }} />}

      <VehicleForm
        form={form}
        onSubmit={onSubmit}
        isSubmitting={createVehicleMutation.isPending}
        submitButtonText={t('routes.$fleetId_vehicles_create.main.actions.create')}
        loadingButtonText={t('routes.$fleetId_vehicles_create.main.actions.creating')}
        isCreateForm={true}
      />
    </Container>
  )
}
